import {
  useToast as useChakraToast,
  UseToastOptions,
  ToastId,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Box,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useRef, useCallback, type ReactNode } from 'react'

export interface ToastOptions extends Omit<UseToastOptions, 'title' | 'description'> {
  titleKey?: string
  descriptionKey?: string
  title?: string
  description?: string
  titleParams?: Record<string, any>
  descriptionParams?: Record<string, any>
  closeOnClick?: boolean
}

export interface SimpleToastOptions {
  title?: string
  titleKey?: string
  description?: string
  descriptionKey?: string
  titleParams?: Record<string, any>
  descriptionParams?: Record<string, any>
  duration?: number
  isClosable?: boolean
  position?: UseToastOptions['position']
  id?: string
}

export interface PromiseToastMessages<T = any> {
  loading?: string | ((data?: T) => string)
  success?: string | ((data: T) => string)
  error?: string | ((error: Error) => string)
  loadingTitleKey?: string
  successTitleKey?: string
  errorTitleKey?: string
}

const extractErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') {
    return null
  }
  const candidate = error as {
    response?: { data?: { error?: { code?: string }; code?: string } }
    code?: string
  }
  return (
    candidate.response?.data?.error?.code ??
    candidate.response?.data?.code ??
    candidate.code ??
    null
  )
}

const extractErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined
  }
  if (typeof error === 'string') {
    return error
  }
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'object') {
    const candidate = error as {
      response?: { data?: { error?: { message?: string }; message?: string } }
      message?: string
      data?: { error?: { message?: string }; message?: string }
    }
    return (
      candidate.response?.data?.error?.message ??
      candidate.response?.data?.message ??
      candidate.data?.error?.message ??
      candidate.data?.message ??
      candidate.message
    )
  }
  return undefined
}

export function useToast() {
  const { t } = useTranslation()
  const chakraToast = useChakraToast()
  const activeToasts = useRef<Set<string | number>>(new Set())
  const promiseToastIds = useRef<Map<Promise<any>, ToastId>>(new Map())

  const showToast = useCallback((options: ToastOptions) => {
    const {
      titleKey,
      descriptionKey,
      title,
      description,
      titleParams,
      descriptionParams,
      id,
      closeOnClick = true,
      ...restOptions
    } = options
    const { render: customRender, ...rest } = restOptions

    const resolvedTitle = titleKey ? t(titleKey, titleParams) : title
    const resolvedDescription = descriptionKey ? t(descriptionKey, descriptionParams) : description
    const defaultRender = (props: {
      id?: ToastId
      status?: UseToastOptions['status']
      variant?: UseToastOptions['variant']
      title?: ReactNode
      description?: ReactNode
      isClosable?: boolean
      onClose: () => void
    }) => {
      const {
        status,
        variant,
        title: toastTitle,
        description: toastDescription,
        isClosable = true,
        onClose,
      } = props
      return (
        <Alert
          status={status}
          variant={variant}
          cursor={closeOnClick ? 'pointer' : undefined}
          onClick={closeOnClick ? onClose : undefined}
        >
          <AlertIcon />
          <Box flex="1" maxW="100%">
            {toastTitle && <AlertTitle>{toastTitle}</AlertTitle>}
            {toastDescription && <AlertDescription display="block">{toastDescription}</AlertDescription>}
          </Box>
          {isClosable && (
            <CloseButton
              size="sm"
              onClick={(event) => {
                event.stopPropagation()
                onClose()
              }}
              position="absolute"
              insetEnd={1}
              top={1}
            />
          )}
        </Alert>
      )
    }

    // Anti-duplicate mechanism: if id is specified and toast already exists, don't show duplicate
    if (id) {
      const toastKey = String(id) // Convert to string for consistent comparison
      if (activeToasts.current.has(toastKey)) {
        return id as ToastId
      }
      activeToasts.current.add(toastKey)
    }

    const toastId = chakraToast({
      position: 'top-right',
      duration: 3000,
      isClosable: true,
      variant: 'solid',
      id,
      ...rest,
      render: customRender
        ? (renderProps) =>
            customRender({
              ...renderProps,
              title: resolvedTitle,
              description: resolvedDescription,
            })
        : (renderProps) =>
            defaultRender({
              ...renderProps,
              title: resolvedTitle,
              description: resolvedDescription,
            }),
      title: resolvedTitle,
      description: resolvedDescription,
      onCloseComplete: () => {
        if (id) {
          activeToasts.current.delete(String(id))
        }
      }
    })

    return toastId
  }, [chakraToast, t])

  // Simplified success toast - supports direct string input
  const success = useCallback((options: Omit<ToastOptions, 'status'> | string) => {
    if (typeof options === 'string') {
      return showToast({ title: options, status: 'success' })
    }
    return showToast({ ...options, status: 'success' })
  }, [showToast, t])

  // Simplified error toast - supports direct string or Error object
  const error = useCallback((options: Omit<ToastOptions, 'status'> | string | Error) => {
    if (typeof options === 'string') {
      return showToast({ title: options, status: 'error', duration: 3000 })
    }
    if (options instanceof Error) {
      const errorCode = extractErrorCode(options)
      if (errorCode === 'feature_mainnet_only') {
        return showToast({
          title: t('featureMainnetOnly'),
          status: 'warning',
          duration: 5000,
        })
      }
      if (errorCode === 'AUTH_FAILED') {
        return showToast({
          title: t('toast.sessionExpired'),
          status: 'warning',
          duration: 5000,
        })
      }
      const description = extractErrorMessage(options)
      return showToast({
        title: t('toast.error'),
        description,
        status: 'error',
        duration: 3000,
      })
    }
    return showToast({ ...options, status: 'error', duration: options.duration ?? 3000 })
  }, [showToast])

  // Simplified warning toast - supports direct string input
  const warning = useCallback((options: Omit<ToastOptions, 'status'> | string) => {
    if (typeof options === 'string') {
      return showToast({ title: options, status: 'warning' })
    }
    return showToast({ ...options, status: 'warning' })
  }, [showToast])

  // Simplified info toast - supports direct string input
  const info = useCallback((options: Omit<ToastOptions, 'status'> | string) => {
    if (typeof options === 'string') {
      return showToast({ title: options, status: 'info' })
    }
    return showToast({ ...options, status: 'info' })
  }, [showToast])

  // Loading toast
  const loading = useCallback((options: Omit<ToastOptions, 'status'> | string) => {
    if (typeof options === 'string') {
      return showToast({ title: options, status: 'loading', duration: null })
    }
    return showToast({ ...options, status: 'loading', duration: null })
  }, [showToast])

  // Promise toast - automatically handles loading, success, and failure states for async operations
  const promise = useCallback(<T = any>(
    promise: Promise<T>,
    messages: PromiseToastMessages<T>
  ): Promise<T> => {
    // Show loading state
    const loadingMessage = messages.loadingTitleKey 
      ? t(messages.loadingTitleKey)
      : (typeof messages.loading === 'function' ? messages.loading() : messages.loading) || 'Loading...'
    
    const loadingToastId = showToast({
      title: loadingMessage,
      status: 'loading',
      duration: null,
    })

    // Save association between promise and toast ID
    promiseToastIds.current.set(promise, loadingToastId)

    return promise
      .then((data) => {
        // Close loading toast
        chakraToast.close(loadingToastId)
        promiseToastIds.current.delete(promise)

        // Show success state
        const successMessage = messages.successTitleKey
          ? t(messages.successTitleKey)
          : (typeof messages.success === 'function' ? messages.success(data) : messages.success) || 'Success!'
        
        showToast({
          title: successMessage,
          status: 'success',
        })

        return data
      })
      .catch((err) => {
        // Close loading toast
        chakraToast.close(loadingToastId)
        promiseToastIds.current.delete(promise)

        // Show error state
        const errorMessage = messages.errorTitleKey
          ? t(messages.errorTitleKey)
          : (typeof messages.error === 'function' ? messages.error(err) : messages.error) || err.message || 'Something went wrong'
        
        showToast({
          title: errorMessage,
          status: 'error',
          duration: 3000,
        })

        throw err
      })
  }, [chakraToast, showToast, t])

  // Close specific toast
  const close = useCallback((toastId: ToastId) => {
    chakraToast.close(toastId)
  }, [chakraToast])

  // Close all toasts
  const closeAll = useCallback(() => {
    chakraToast.closeAll()
    activeToasts.current.clear()
    promiseToastIds.current.clear()
  }, [chakraToast])

  // Update existing toast
  const update = useCallback((toastId: ToastId, options: UseToastOptions) => {
    chakraToast.update(toastId, options)
  }, [chakraToast])

  // Check if toast is active
  const isActive = useCallback((toastId: ToastId) => {
    return chakraToast.isActive(toastId)
  }, [chakraToast])

  return {
    // Full functionality
    toast: showToast,

    // Simplified status methods
    success,
    error,
    warning,
    info,
    loading,

    // Promise handling
    promise,

    // Control methods
    close,
    closeAll,
    update,
    isActive,
  }
}

export const useAppToast = useToast
